/* eslint-disable camelcase */
import { Button, RadioInput, SelectInput, TextInput, useButtonConfirmDialog, useForm } from '@rocketmakers/armstrong';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router';
import { RuleCategory } from '../../../../../backend/shared/api-clients/_api/config';
import {
  Amperage,
  CircuitType,
  InlineMeterIem2055,
  InlineMeterIem2155,
  ModbusAddresses45AmpMeter,
  ModbusAddresses63AmpMeter,
  RuleActionType,
} from '../../../../shared/api-clients/_api';
import { useRoutePaths } from '../../../../shared/context/paths';
import { enumToArray, generateSelectOptions } from '../../../../shared/helpers/enumHelpers';
import { useValidatedForm } from '../../../../shared/hooks/formValidationHook';
import { InputValidator } from '../../../../shared/hooks/ValidationTypes';
import { requiredTextInputValidator } from '../../../../shared/hooks/Validators';
import { BackButton } from '../../components/buttons/back-button';
import { Spinner } from '../../components/spinner/spinner';
import { useBeeConfig } from '../../hooks/beeConfig';
import { routePaths } from '../../routes';
import './configure-meter.scss';
import { amperageToMeterTypeMap, InlineMeterDevice } from './types';

interface IInlineMeterForm {
  circuitName: string;
  circuitType: CircuitType;
  amperage: Amperage;
  modbusAddress: ModbusAddresses45AmpMeter | ModbusAddresses63AmpMeter;
}

const initialState: IInlineMeterForm = {
  circuitName: '',
  circuitType: null,
  amperage: null,
  modbusAddress: null,
};

const amp45Addresses = generateSelectOptions(ModbusAddresses45AmpMeter, true);
const amp63Addresses = generateSelectOptions(ModbusAddresses63AmpMeter, true);
const amperageOptions = enumToArray(Amperage, true).map(amperage => amperage.toString());
const circuitOptions = enumToArray(
  CircuitType,
  false,
  CircuitType.REGULATED,
  CircuitType.UNREGULATED,
  CircuitType.MAIN_INCOMING_SUPPLY
);

const is45AmpMeter = (value: string) => +value === Amperage.inlineMeter45amp;

export const validators: InputValidator<IInlineMeterForm>[] = [
  {
    key: 'circuitName',
    validators: [requiredTextInputValidator],
  },
  {
    key: 'circuitType',
    validators: [
      {
        validate: value => circuitOptions.includes(value),
        message: 'Please select a Circuit Type',
      },
    ],
  },
  {
    key: 'amperage',
    validators: [
      {
        validate: value => amperageOptions.includes(typeof value === 'number' ? value.toString() : value),
        message: 'Please select a meter amperage',
      },
    ],
  },
  {
    key: 'modbusAddress',
    validators: [
      {
        validate: (value, dataBinder) =>
          (is45AmpMeter(dataBinder.getValue('amperage'))
            ? enumToArray(ModbusAddresses45AmpMeter, true)
            : enumToArray(ModbusAddresses63AmpMeter, true)
          ).includes(parseInt(value, 10)),
        message: 'Please select a Modbus Address',
      },
    ],
  },
];

export const ConfigureInlineMeterView: React.FunctionComponent = () => {
  const { state } = useLocation<InlineMeterDevice>();
  const history = useHistory();
  const { createDevice, removeDevice, editDevice, loading } = useBeeConfig();
  const { replace } = useRoutePaths();

  const initState = React.useMemo(() => {
    return state
      ? {
          circuitName: state.params.circuit_label,
          circuitType: state.params.circuit_type,
          amperage: state.params.meter_amperage,
          modbusAddress: state.params.address,
        }
      : initialState;
  }, [state]);

  const { DataForm, bind, dataBinder } = useForm(initState);
  const { setTouched, formIsValid, validationErrors } = useValidatedForm<IInlineMeterForm>(validators, dataBinder);

  const handleContinue = () => {
    const meter_amperage = +dataBinder.getValue('amperage');
    const deviceType = amperageToMeterTypeMap[meter_amperage];

    const inlineMeter: InlineMeterIem2055 | InlineMeterIem2155 = {
      deviceType,
      params: {
        address: +dataBinder.getValue('modbusAddress'),
        circuit_type: dataBinder.getValue('circuitType'),
        circuit_label: dataBinder.getValue('circuitName'),
        meter_amperage,
        category: RuleCategory.ELECTRICITY,
        action_type: RuleActionType.MONITORING,
        channel: 'C1',
      },
    };

    if (state) {
      editDevice(state, inlineMeter);
    } else {
      createDevice(inlineMeter);
    }
    replace(routePaths.inlineMeters.root);
  };

  const deletable = useButtonConfirmDialog({
    content: `Are you sure you want to delete ${state?.params?.circuit_label}?`,
  });

  const handleDelete = () => {
    removeDevice(state);
    history.go(-1);
  };

  return (
    <DataForm validationResults={validationErrors} validationMode="both">
      {loading && <Spinner />}
      <BackButton />
      <h1>
        Configuration <span className="color-primary">Inline Electricity Meters</span>
      </h1>
      <div className="configure-inline-meter__circuit-name">
        <TextInput
          {...bind.select('circuitName')}
          label="What circuit is the meter measuring"
          onBlur={setTouched}
          data-qa="configure-inline-meter-circuit-name"
        />
        <p className="configure-inline-meter__circuit-name__helper-text">
          This should be an easy to understand general label such as "Hot Water' or "Downstairs Sockets"
        </p>
      </div>
      <div>
        <label className="armstrong-label">What is the type of circuit?</label>
        <div className="configure-inline-meter__radio-group">
          {circuitOptions.map(circuitType => (
            <RadioInput
              key={circuitType}
              labelContent={circuitType}
              {...bind.radio('circuitType', circuitType)}
              onBlur={setTouched}
              data-qa={`configure-inline-meter-circuitType__${circuitType}`}
            />
          ))}
        </div>
      </div>
      <div>
        <label className="armstrong-label">What is the Amperage of the meter?</label>
        <div className="configure-inline-meter__radio-group">
          {amperageOptions.map(amperage => (
            <RadioInput
              key={amperage}
              labelContent={amperage}
              {...bind.radio('amperage', amperage)}
              onBlur={setTouched}
              data-qa={`configure-inline-meter-amperage__${amperage}`}
            />
          ))}
        </div>
      </div>
      <div className="row">
        <SelectInput
          {...bind.select('modbusAddress')}
          label="Enter the MODBUS address:"
          options={is45AmpMeter(dataBinder.getValue('amperage')) ? amp45Addresses : amp63Addresses}
          onBlur={setTouched}
          data-qa="configure-inline-meter-modbus-address"
        />
      </div>
      <div className="row">
        <span className="spacer" />
        {state && (
          <Button
            className="btn-full btn-large secondary-button"
            onClick={async () => {
              if (await deletable()) {
                handleDelete();
              }
            }}
            data-qa="delete-button"
          >
            Delete circuit
          </Button>
        )}
        <Button
          className="continue-button btn-full btn-large"
          disabled={formIsValid === false}
          onClick={handleContinue}
          data-qa="continue-button"
        >
          {state ? 'Save changes' : 'Save'}
        </Button>
      </div>
    </DataForm>
  );
};
